/**
 * ShareDialog.tsx
 * React component for flowchart sharing dialog
 * Fully typed TypeScript implementation
 */

import React, { useState, useEffect, FormEvent } from 'react';
import { logger } from './lib/logger';
import { 
  X, 
  Copy, 
  Check, 
  Mail, 
  Link as LinkIcon, 
  UserPlus, 
  Crown, 
  Edit3, 
  Eye, 
  MoreVertical 
} from 'lucide-react';
import {
  getUserRole,
  getFlowchartUsers,
  updateUserRole,
  removeAccess,
  setLinkSharing,
  getShareLink,
  sendInvitation,
  getPendingInvitations,
  cancelInvitation,
  getRoleBadge
} from './types/sharingUtils';
import { useT } from './i18n/index';
import type {
  ShareDialogProps,
  UserAccessInfo,
  PendingInvitation,
  UserRole,
} from './types';

/**
 * Extended role badge with Tailwind classes
 */
interface ExtendedRoleBadge {
  icon: JSX.Element;
  label: string;
  bg: string;
  text: string;
}

/**
 * ShareDialog Component
 */
function ShareDialog({ flowchartId, flowchartTitle, user, onClose }: ShareDialogProps): JSX.Element {
  const { t } = useT();
  // State
  const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);
  const [users, setUsers] = useState<UserAccessInfo[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [linkSharingEnabled, setLinkSharingEnabled] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteRole, setInviteRole] = useState<UserRole>('editor');
  const [inviteMessage, setInviteMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [userMenuOpen, setUserMenuOpen] = useState<string | null>(null);

  /**
   * Load all sharing data
   */
  const loadData = async (): Promise<void> => {
    setLoading(true);
    
    try {
      // Get current user's role
      const role = await getUserRole(flowchartId);
      setCurrentUserRole(role);

      // Get all users with access
      const userList = await getFlowchartUsers(flowchartId);
      setUsers(userList);

      // Get pending invitations
      const invites = await getPendingInvitations(flowchartId);
      setPendingInvites(invites);

      // Get share link if exists
      const link = await getShareLink(flowchartId);
      if (link) {
        setShareLink(link);
        setLinkSharingEnabled(true);
      }
    } catch (error) {
      logger.error('Error loading share data:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load data when component mounts or flowchart changes
   */
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowchartId]);

  /**
   * Copy share link to clipboard
   */
  const handleCopyLink = async (): Promise<void> => {
    try {
      if (!shareLink) {
        // Enable link sharing first
        const success = await setLinkSharing(flowchartId, true);
        if (success) {
          const link = await getShareLink(flowchartId);
          if (link) {
            setShareLink(link);
            setLinkSharingEnabled(true);
            
            // Copy to clipboard
            await navigator.clipboard.writeText(link);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
          }
        }
      } else {
        await navigator.clipboard.writeText(shareLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
    } catch (error) {
      logger.error('Error copying link:', error);
      alert(t('failedCopyLink'));
    }
  };

  /**
   * Toggle link sharing on/off
   */
  const handleToggleLinkSharing = async (): Promise<void> => {
    const newState = !linkSharingEnabled;
    const success = await setLinkSharing(flowchartId, newState);
    
    if (success) {
      setLinkSharingEnabled(newState);
      if (newState) {
        const link = await getShareLink(flowchartId);
        if (link) {
          setShareLink(link);
        }
      } else {
        setShareLink('');
      }
    } else {
      alert(t('failedUpdateLinkSharing'));
    }
  };

  /**
   * Send email invitation
   */
  const handleSendInvite = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!inviteEmail) return;

    const result = await sendInvitation(flowchartId, inviteEmail, inviteRole, inviteMessage);
    
    if (result.success) {
      setInviteEmail('');
      setInviteMessage('');
      await loadData(); // Reload to show new invite
      alert(t('invitationSent', { email: inviteEmail }));
    } else {
      alert(t('invitationFailed', { error: result.error || 'Unknown error' }));
    }
  };

  /**
   * Update user's role
   */
  const handleUpdateRole = async (userId: string, newRole: UserRole): Promise<void> => {
    const success = await updateUserRole(flowchartId, userId, newRole);
    if (success) {
      await loadData();
    } else {
      alert(t('failedUpdateRole'));
    }
  };

  /**
   * Remove user's access
   */
  const handleRemoveUser = async (userId: string): Promise<void> => {
    if (!window.confirm(t('confirmRemoveUserAccess'))) return;

    const success = await removeAccess(flowchartId, userId);
    if (success) {
      await loadData();
    } else {
      alert(t('failedRemoveUser'));
    }
  };

  /**
   * Cancel pending invitation
   */
  const handleCancelInvite = async (inviteId: string): Promise<void> => {
    const success = await cancelInvitation(inviteId);
    if (success) {
      await loadData();
    } else {
      alert(t('failedCancelInvitation'));
    }
  };

  /**
   * Get role icon component
   */
  const getRoleIcon = (role: UserRole): JSX.Element => {
    const badge = getRoleBadge(role);
    
    switch (badge.icon) {
      case 'crown':
        return <Crown size={16} className={badge.color} />;
      case 'edit':
        return <Edit3 size={16} className={badge.color} />;
      case 'eye':
        return <Eye size={16} className={badge.color} />;
      default:
        return <Eye size={16} className={badge.color} />;
    }
  };

  /**
   * Get extended role badge with Tailwind classes
   */
  const getExtendedRoleBadge = (role: UserRole): ExtendedRoleBadge => {
    const badge = getRoleBadge(role);
    
    const colorClasses: Record<UserRole, { bg: string; text: string }> = {
      owner: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      editor: { bg: 'bg-blue-100', text: 'text-blue-800' },
      viewer: { bg: 'bg-gray-100', text: 'text-gray-800' },
    };

    return {
      icon: getRoleIcon(role),
      label: badge.label,
      bg: colorClasses[role].bg,
      text: colorClasses[role].text,
    };
  };

  // Check if current user is owner
  const isOwner = currentUserRole === 'owner';

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={onClose}
    >
      {loading ? (
        <div style={{ background: 'white', borderRadius: 12, padding: 24 }} onClick={e => e.stopPropagation()}>
          <p>{t('loading')}</p>
        </div>
      ) : (
        <div
          style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.18)', animation: 'modalSlideIn 0.18s ease-out' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{t('share')}</h2>
              <p className="text-sm text-gray-600 mt-1">{flowchartTitle}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Link Sharing */}
            {isOwner && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <LinkIcon size={20} className="text-blue-600" />
                    <h3 className="font-semibold text-gray-800">{t('shareLink')}</h3>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={linkSharingEnabled} onChange={handleToggleLinkSharing} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {linkSharingEnabled && shareLink && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareLink}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded bg-white text-sm"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2"
                    >
                      {linkCopied ? <Check size={16} /> : <Copy size={16} />}
                      {linkCopied ? t('copied') : t('copy')}
                    </button>
                  </div>
                )}

                {!linkSharingEnabled && (
                  <p className="text-sm text-gray-600">
                    {t('enableLinkSharingHint')}
                  </p>
                )}
              </div>
            )}

            {/* Email Invite */}
            {isOwner && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mail size={20} className="text-blue-600" />
                  <h3 className="font-semibold text-gray-800">{t('inviteByEmail')}</h3>
                </div>
                
                <form onSubmit={handleSendInvite} className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded text-sm"
                      required
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as UserRole)}
                      className="px-3 py-2 border rounded text-sm"
                    >
                      <option value="editor">{t('editor')}</option>
                      <option value="viewer">{t('viewer')}</option>
                      <option value="owner">{t('owner')}</option>
                    </select>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                      {t('send')}
                    </button>
                  </div>
                  
                  <input
                    type="text"
                    placeholder={t('addMessageOptional')}
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </form>
              </div>
            )}

            {/* People with Access */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <UserPlus size={20} className="text-blue-600" />
                {t('peopleWithAccess')} ({users.length})
              </h3>

              <div className="space-y-2">
                {users.map((accessUser) => {
                  const badge = getExtendedRoleBadge(accessUser.role);
                  const isCurrentUser = accessUser.user_id === user.id;
                  const displayName = accessUser.email || accessUser.user_id;
                  const isProjectInherited = accessUser.via_project === true;

                  return (
                    <div key={accessUser.user_id} className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
                          {displayName.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800">
                            {displayName}
                            {isCurrentUser && <span className="text-gray-500 ml-1">{t('you')}</span>}
                          </div>
                          <div className="text-xs text-gray-500">
                            {isProjectInherited
                              ? t('viaProjectSharing')
                              : t('addedOn', { date: new Date(accessUser.granted_at).toLocaleDateString() })}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text} flex items-center gap-1`}>
                          {badge.icon}
                          {badge.label}
                        </span>

                        {isProjectInherited && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            {t('projectBadge')}
                          </span>
                        )}

                        {isOwner && !isCurrentUser && !isProjectInherited && (
                          <div className="relative">
                            <button
                              onClick={() => setUserMenuOpen(userMenuOpen === accessUser.user_id ? null : accessUser.user_id)}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <MoreVertical size={16} />
                            </button>

                            {userMenuOpen === accessUser.user_id && (
                              <div className="absolute right-0 mt-1 w-48 bg-white border rounded shadow-lg z-10">
                                {accessUser.role !== 'owner' && (
                                  <button
                                    onClick={() => {
                                      handleUpdateRole(accessUser.user_id, 'owner');
                                      setUserMenuOpen(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Crown size={14} />
                                    {t('makeOwner')}
                                  </button>
                                )}
                                {accessUser.role === 'owner' && (
                                  <button
                                    onClick={() => {
                                      handleUpdateRole(accessUser.user_id, 'editor');
                                      setUserMenuOpen(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Edit3 size={14} />
                                    {t('changeToEditor')}
                                  </button>
                                )}
                                {accessUser.role === 'editor' && (
                                  <button
                                    onClick={() => {
                                      handleUpdateRole(accessUser.user_id, 'viewer');
                                      setUserMenuOpen(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Eye size={14} />
                                    {t('changeToViewer')}
                                  </button>
                                )}
                                {accessUser.role === 'viewer' && (
                                  <button
                                    onClick={() => {
                                      handleUpdateRole(accessUser.user_id, 'editor');
                                      setUserMenuOpen(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                                  >
                                    <Edit3 size={14} />
                                    {t('changeToEditor')}
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    handleRemoveUser(accessUser.user_id);
                                    setUserMenuOpen(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600 flex items-center gap-2"
                                >
                                  <X size={14} />
                                  {t('removeAccess')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pending Invitations */}
            {isOwner && pendingInvites.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">{t('pendingInvitations')} ({pendingInvites.length})</h3>
                <div className="space-y-2">
                  {pendingInvites.map((invite) => {
                    const badge = getExtendedRoleBadge(invite.role);
                    return (
                      <div key={invite.id} className="flex items-center justify-between py-2 px-3 bg-yellow-50 rounded">
                        <div className="flex items-center gap-3">
                          <Mail size={16} className="text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-800">{invite.email}</div>
                            <div className="text-xs text-gray-500">
                              {t('invitedOn', { date: new Date(invite.created_at).toLocaleDateString() })}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                          <button
                            onClick={() => handleCancelInvite(invite.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              {currentUserRole ? (
                <>
                  {getRoleIcon(currentUserRole)}
                  <span>{t('youAreRole', { article: currentUserRole === 'owner' ? 'an' : 'a', role: t(currentUserRole) })}</span>
                </>
              ) : (
                <span>{t('youHaveNoAccess')}</span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShareDialog;
